import { useBankProfile } from '@contexts/BankProfileContext';
import type { OPERATION_TYPE } from '@pages/batch-operations/utils';
import apiClient from '@services/apiClient';
import { useQuery } from '@tanstack/react-query';
import type { OperationStatus } from 'types/operations';

import type { PaginatedItems } from './common';
import type { Transaction } from './types/getTransactions';
import { Direction } from './types/getTransactions';

export const OPERATIONS_PAGE_SIZE = 20;

interface UseOperationsParams {
	page?: number;
	limit?: number;
	dateFrom?: string;
	dateTo?: string;
	status?: OperationStatus[];
	paymentType?: OPERATION_TYPE;
	documentNumber?: string;
	senderIban?: string;
	beneficiaryIban?: string;
	beneficiaryName?: string;
	direction?: Direction;
}

const fetchOperations = async (
	profileId: string,
	page: number,
	limit: number,
	filters: Omit<UseOperationsParams, 'page' | 'limit'>,
): Promise<PaginatedItems<Transaction[]>> => {
	const response = await apiClient.get(`profiles/${profileId}/transactions`, {
		params: {
			page,
			per_page: limit,
			sort: '-id',
			...(filters.dateFrom ? { 'filter[created_at][from]': filters.dateFrom } : {}),
			...(filters.dateTo ? { 'filter[created_at][to]': filters.dateTo } : {}),
			...(filters.status?.length ? { 'filter[statuses]': filters.status } : {}),
			...(filters.paymentType ? { 'filter[payment_type]': filters.paymentType } : {}),
			...(filters.documentNumber
				? { 'filter[document_number]': filters.documentNumber }
				: {}),
			...(filters.senderIban
				? { 'filter[payer_account_number]': filters.senderIban }
				: {}),
			...(filters.beneficiaryIban
				? { 'filter[beneficiary_account_number]': filters.beneficiaryIban }
				: {}),
			...(filters.beneficiaryName
				? { 'filter[beneficiary_account_name]': filters.beneficiaryName }
				: {}),
			...(filters.direction ? { 'filter[direction]': filters.direction } : {}),
		},
	});

	return response.data;
};

export const useOperations = ({
	page = 1,
	limit = OPERATIONS_PAGE_SIZE,
	dateFrom,
	dateTo,
	status,
	paymentType,
	documentNumber,
	senderIban,
	beneficiaryIban,
	beneficiaryName,
	direction,
}: UseOperationsParams = {}) => {
	const { selectedProfile } = useBankProfile();

	return useQuery({
		queryKey: [
			'operations',
			selectedProfile?.id,
			page,
			limit,
			dateFrom,
			dateTo,
			status,
			paymentType,
			documentNumber,
			senderIban,
			beneficiaryIban,
			beneficiaryName,
			direction,
		],
		queryFn: () =>
			fetchOperations(selectedProfile!.id, page, limit, {
				dateFrom,
				dateTo,
				status,
				paymentType,
				documentNumber,
				senderIban,
				beneficiaryIban,
				beneficiaryName,
				direction,
			}),
		enabled: !!selectedProfile,
		select: (data) => ({
			...data,
			items: data.items.map((transaction) => ({
				...transaction,
				amount: transaction.amount / 100,
			})),
		}),
	});
};
